import React, { useCallback, useEffect, useState } from 'react';
import { fetchMyUplinkNotifications, MyUplinkAlarm } from '../../services/api';
import { formatInTZ } from '../../utils/timezone';
import { ui } from '../../styles/shared';
import DisabledNotice from './DisabledNotice';

interface Props {
    enabled: boolean;
    timezone: string;
    onError: (msg: string) => void;
    onGoToStatus: () => void;
}

const SEVERITY_LABELS = ['Info', 'Varning', 'Fel', 'Kritiskt'] as const;
const severityLabel = (s: number): string => SEVERITY_LABELS[Math.min(Math.max(s, 0), 3)];
const severityColor = (s: number): string =>
    s >= 3 ? '#dc3545' : s === 2 ? '#fd7e14' : s === 1 ? '#ffc107' : '#17a2b8';

// Litet färgat märke, t.ex. allvarlighet eller läge.
const Pill: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', backgroundColor: `${color}18`, color }}>
        {children}
    </span>
);

const cell: React.CSSProperties = { padding: '8px 12px' };

// Flik: aktiva larm och alla meddelanden från myUplink.
const AlarmsTab: React.FC<Props> = ({ enabled, timezone, onError, onGoToStatus }) => {
    const [notifications, setNotifications] = useState<MyUplinkAlarm[]>([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback((reportError: boolean) => {
        setLoading(true);
        fetchMyUplinkNotifications()
            .then(h => setNotifications(h))
            .catch(e => { if (reportError) onError('Kunde inte hämta meddelanden: ' + e.message); })
            .finally(() => setLoading(false));
    }, [onError]);

    // Hämta meddelanden när fliken öppnas
    useEffect(() => {
        if (enabled) load(true);
    }, [enabled, load]);

    const active = notifications.filter(n => n.status?.toLowerCase() === 'active');

    return (
        <div>
            {!enabled ? (
                <DisabledNotice onGoToStatus={onGoToStatus} />
            ) : loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Hämtar…</div>
            ) : active.length === 0 ? (
                <div style={{ ...ui.card, textAlign: 'center', color: '#28a745', fontSize: '15px', marginBottom: '16px' }}>
                    ✅ Inga aktiva larm
                </div>
            ) : (
                <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden', marginBottom: '24px' }}>
                    <div style={{ padding: '12px 16px', backgroundColor: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
                        <span style={{ fontWeight: 'bold', color: '#856404' }}>⚠️ {active.length} {active.length === 1 ? 'aktivt larm' : 'aktiva larm'}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                {['#', 'Allvarlighet', 'Rubrik', 'Utrustning', 'System', 'Sedan'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 'bold', color: '#555', borderBottom: '1px solid #ddd' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {active.map((a, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                                    <td style={{ ...cell, color: '#888', fontFamily: 'monospace' }}>{a.alarmNumber}</td>
                                    <td style={cell}><Pill color={severityColor(a.severity)}>{severityLabel(a.severity)}</Pill></td>
                                    <td style={{ ...cell, fontWeight: 'bold' }} title={a.description || undefined}>{a.header}</td>
                                    <td style={{ ...cell, color: '#666' }}>{a.equipName || '—'}</td>
                                    <td style={{ ...cell, color: '#666' }}>{a.systemName || a.systemId}</td>
                                    <td style={{ ...cell, color: '#aaa', fontSize: '12px', whiteSpace: 'nowrap' }}>{a.createdDatetime ? formatInTZ(a.createdDatetime, timezone) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Meddelanden */}
            <div style={{ ...ui.card, marginTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0 }}>🔔 Meddelanden</h3>
                    <button onClick={() => load(false)} style={ui.btn('#6c757d')}>↻ Uppdatera</button>
                </div>
                {!enabled ? (
                    <div style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: '12px 0' }}>myUplink är avstängt.</div>
                ) : loading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Hämtar…</div>
                ) : notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: '12px 0' }}>Inga meddelanden.</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                {['Läge', 'Allvarlighet', 'Rubrik', 'Utrustning', 'System', 'Skapat'].map(h => (
                                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', color: '#555' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {notifications.map((n, i) => {
                                const s = n.status?.toLowerCase() ?? '';
                                const isActive = s.includes('active') && !s.includes('dismissed') && !s.includes('reset');
                                return (
                                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                                        <td style={cell}><Pill color={isActive ? '#dc3545' : '#6c757d'}>{n.status}</Pill></td>
                                        <td style={cell}><Pill color={severityColor(n.severity)}>{severityLabel(n.severity)}</Pill></td>
                                        <td style={{ ...cell, fontWeight: 'bold' }} title={n.description || undefined}>{n.header}</td>
                                        <td style={{ ...cell, color: '#666' }}>{n.equipName || '—'}</td>
                                        <td style={{ ...cell, color: '#666' }}>{n.systemName || n.systemId}</td>
                                        <td style={{ ...cell, color: '#888', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                            {n.createdDatetime ? formatInTZ(n.createdDatetime, timezone) : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AlarmsTab;
